import { QueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Shared data layer
// ---------------------------------------------------------------------------
// One QueryClient backs every cached read in the app. Service functions in
// recharge.js / shopify.js route their fetches through `cachedFetch`, so all
// pages and components share a single cache with in-flight deduplication,
// per-entity staleness, and prefix-based invalidation — regardless of whether
// they call the services imperatively or (later) through React Query hooks.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Preserve the previous service-layer behavior: no automatic retries,
      // no background refetching. Mutations invalidate explicitly.
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const DEFAULT_TTL_MS = 30_000; // matches the old recharge.js cache TTL
const MIN_GC_MS = 5 * 60_000;

// structuredClone is missing on Safari < 15.4 (iPhones/iPads stuck on
// iOS 15.0–15.3), where it threw "Can't find variable: structuredClone" on
// every cached read and crashed the Overview (WIL-104). The cache only holds
// JSON API payloads, so a JSON round-trip is a lossless fallback.
const deepClone = (data) => (
  typeof structuredClone === 'function'
    ? structuredClone(data)
    : JSON.parse(JSON.stringify(data))
);

/**
 * Fetch through the shared query cache.
 *
 * - Concurrent callers with the same key share one request.
 * - Results are served from cache for `ttlMs` (staleTime), then refetched on
 *   the next call.
 * - Returns a deep clone: callers throughout the app mutate results in place
 *   (e.g. Overview's subscription enrichment) and must not corrupt the shared
 *   cache entry.
 *
 * @param {Array} queryKey - e.g. ['subscriptions', options]. The first element
 *   is the entity name used by `invalidateSharedCache`.
 * @param {Function} fetcher - async function performing the network request.
 * @param {number} [ttlMs] - how long the result is considered fresh.
 */
export const cachedFetch = async (queryKey, fetcher, ttlMs = DEFAULT_TTL_MS) => {
  const data = await queryClient.fetchQuery({
    queryKey,
    queryFn: fetcher,
    staleTime: ttlMs,
    gcTime: Math.max(ttlMs, MIN_GC_MS),
  });
  return deepClone(data);
};

/**
 * Drop cached entries so the next read fetches fresh data. Pass an entity
 * name ('subscriptions', 'charges', …) to invalidate every cached variant of
 * that entity, or nothing to wipe the whole cache (e.g. on a new session).
 */
export const invalidateSharedCache = (entity) => {
  if (!entity) {
    queryClient.removeQueries();
    return;
  }
  queryClient.removeQueries({ queryKey: [entity] });
};
